import React from "react";
import { cn } from "../../utils/cn";
import { SpinnerIcon } from "@/components/icons";
import { BRAND_GRADIENT_HOVER, BRAND_GRADIENT_TRACK } from "@/components/ui/brandGradient";

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: "primary" | "secondary" | "ghost" | "destructive";
  size?: "sm" | "md" | "lg";
  isLoading?: boolean;
}

/**
 * Every button wears the brand gradient (J13), so buttons share one style everywhere: page headers, drawer footers,
 * forms. The main action reads darker and bolder. Ghost is text only, and destructive stays red.
 */
export const Button: React.FC<ButtonProps> = ({
  className,
  variant = "primary",
  size = "md",
  isLoading,
  disabled,
  children,
  ...props
}) => {
  return (
    <button
      className={cn(
        // Base styles
        "inline-flex items-center justify-center rounded-lg font-medium transition-colors",
        "focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-offset-2",
        "disabled:pointer-events-none disabled:opacity-50",

        // Variants
        {
          [`${BRAND_GRADIENT_TRACK} ${BRAND_GRADIENT_HOVER} font-semibold text-indigo-950 focus-visible:ring-indigo-400`]:
            variant === "primary",
          [`${BRAND_GRADIENT_TRACK} ${BRAND_GRADIENT_HOVER} text-gray-700 focus-visible:ring-indigo-400`]:
            variant === "secondary",
          "hover:bg-gray-100 focus-visible:ring-gray-500": variant === "ghost",
          "bg-red-600 text-white hover:bg-red-700 focus-visible:ring-red-500":
            variant === "destructive",
        },

        // Sizes
        {
          "h-8 px-3 text-sm": size === "sm",
          "h-10 px-4": size === "md",
          "h-12 px-6 text-lg": size === "lg",
        },

        className,
      )}
      disabled={disabled || isLoading}
      {...props}
    >
      {isLoading && <SpinnerIcon className="mr-2 h-4 w-4" />}
      {children}
    </button>
  );
};
